import clsx from "clsx";
import React from "react";
import { Menu, MenuProps, MenuItem, IconButton } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { MoreHoriz } from "@mui/icons-material";
import { ConfirmActionDialog } from "../dialog/ConfirmActionDialog";
import { useDeleteLibraryExercise } from "./hooks/useDeleteLibraryExercise";
import useArchiveRestoreLibraryExerciseMutation from "./hooks/useArchiveRestoreLibraryExercise";
import {
  BodypartType,
  ExerciseAssetStatus,
  NotificationExerciseBriefDto,
} from "@growth-machine-llc/stridist-api-client";
import { useDirectApprovePendingExerciseLibraryAsset } from "./hooks/useDirectApprovePendingExerciseLibraryAsset";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { TeamRole } from "../../constants";
import { useToggleTeamExerciseVisibilityMutation } from "./hooks/useToggleTeamExerciseVisibility";
import { useExerciseListFilterContext } from "../../contexts/ExerciseLibraryFilterContext";
import { useValidateBodyparts } from "./hooks/useValidateBodypart";

const useStyles = makeStyles(() => ({
  root: {},
}));

export interface ExercisesMenuProps
  extends Omit<MenuProps, "open" | "onClose"> {
  exerciseId: number;
  archived: boolean;
  pendingApproval?: boolean;
  isTeamShared?: boolean;
  exerciseNotification?: NotificationExerciseBriefDto;
  exerciseCoachId?: number;
  bodyparts: BodypartType[];
  onExerciseSelected: () => void;
}

export function ExercisesMenu(props: ExercisesMenuProps) {
  const {
    className,
    exerciseId,
    archived,
    pendingApproval,
    exerciseNotification,
    isTeamShared,
    exerciseCoachId,
    onExerciseSelected,
    bodyparts,
    ...other
  } = props;

  const user = useCurrentUser();

  const validateBodyparts = useValidateBodyparts();

  const { exerciseType } = useExerciseListFilterContext();
  const shareExerciseEnabled =
    user?.teamRole === TeamRole.OWNER || user?.id === exerciseCoachId;

  const { mutate: deleteExercise } = useDeleteLibraryExercise(
    pendingApproval,
    exerciseNotification,
  );
  const { mutate: restore } = useArchiveRestoreLibraryExerciseMutation(
    ExerciseAssetStatus.ARCHIVED,
    bodyparts,
  );
  const { mutate: archive } = useArchiveRestoreLibraryExerciseMutation(
    ExerciseAssetStatus.PUBLISHED,
  );

  const { mutate: toggleTeamExerciseVisibility } =
    useToggleTeamExerciseVisibilityMutation(exerciseType);

  const { mutate: approve } =
    useDirectApprovePendingExerciseLibraryAsset(exerciseNotification);

  const s = useStyles();
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );
  const [confirmExerciseDeleteDialogOpen, setConfirmExerciseDeleteDialogOpen] =
    React.useState(false);

  const handleOpen = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleClose = React.useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleExerciseDelete = React.useCallback(() => {
    setAnchorEl(null);
    setConfirmExerciseDeleteDialogOpen(true);
  }, [exerciseId, setConfirmExerciseDeleteDialogOpen]);

  const handleExerciseArchive = React.useCallback(() => {
    setAnchorEl(null);
    archive({ id: exerciseId });
  }, [exerciseId, setConfirmExerciseDeleteDialogOpen]);

  const handleConfirmExerciseDelete = React.useCallback(() => {
    deleteExercise(exerciseId);
    setConfirmExerciseDeleteDialogOpen(false);
  }, [exerciseId, deleteExercise]);

  const handleCancelExerciseDelete = React.useCallback(() => {
    setConfirmExerciseDeleteDialogOpen(false);
  }, []);

  const handleExerciseRestore = React.useCallback(() => {
    setAnchorEl(null);
    const isValidBodyparts = validateBodyparts(bodyparts);
    if (!isValidBodyparts) {
      onExerciseSelected();
      return;
    }
    restore({ id: exerciseId });
  }, [exerciseId, restore, validateBodyparts, bodyparts]);

  const handleExerciseApprove = React.useCallback(() => {
    setAnchorEl(null);
    const isValidBodyparts = validateBodyparts(bodyparts);
    if (!isValidBodyparts) {
      onExerciseSelected();
      return;
    }
    approve({ id: exerciseId });
  }, [exerciseId, approve, validateBodyparts, bodyparts]);

  const handleToggleTeamExerciseVisibility = React.useCallback(() => {
    setAnchorEl(null);
    toggleTeamExerciseVisibility({
      id: exerciseId,
      isTeamShared: Boolean(!isTeamShared),
    });
  }, [exerciseId, toggleTeamExerciseVisibility, isTeamShared]);

  if (
    user?.teamRole === TeamRole.OWNER &&
    user?.id !== exerciseCoachId &&
    !isTeamShared
  ) {
    return null;
  }

  return (
    <>
      <IconButton onClick={handleOpen} size="large">
        <MoreHoriz />
      </IconButton>
      <Menu
        id={exerciseId.toString()}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        keepMounted
        onClose={handleClose}
        className={clsx(s.root, className)}
        {...other}
      >
        {archived ? (
          <>
            <MenuItem onClick={handleExerciseRestore}>Restore</MenuItem>
            <MenuItem onClick={handleExerciseDelete}>Delete</MenuItem>
          </>
        ) : pendingApproval ? (
          <>
            <MenuItem onClick={handleExerciseApprove}>Approve</MenuItem>
            <MenuItem onClick={handleExerciseDelete}>Delete</MenuItem>
          </>
        ) : (
          <>
            {user?.teamRole === TeamRole.OWNER &&
            user?.id !== exerciseCoachId ? (
              isTeamShared && (
                <MenuItem onClick={handleToggleTeamExerciseVisibility}>
                  Remove from Team
                </MenuItem>
              )
            ) : (
              <>
                {shareExerciseEnabled && (
                  <MenuItem onClick={handleToggleTeamExerciseVisibility}>
                    {isTeamShared ? "Remove from Team" : "Share with Team"}
                  </MenuItem>
                )}
                <MenuItem onClick={handleExerciseArchive}>Archive</MenuItem>
              </>
            )}
          </>
        )}
      </Menu>

      <ConfirmActionDialog
        title="Are you sure you want to delete exercise?"
        open={confirmExerciseDeleteDialogOpen}
        onClose={handleCancelExerciseDelete}
        onCancel={handleCancelExerciseDelete}
        onConfirm={handleConfirmExerciseDelete}
      />
    </>
  );
}
